"use client";
import { useEffect, useRef, useState } from "react";
import { CheckCircle2Icon } from "lucide-react";
import Lottie from "lottie-react";
import { tabContent } from "./tabContent";

const lottieCache = {};
const MiddleLottie = ({ path }) => {
  const [data, setData] = useState(lottieCache[path] ?? null);
  useEffect(() => {
    if (!path || lottieCache[path]) return;
    let cancelled = false;
    fetch(path)
      .then((r) => r.json())
      .then((json) => {
        lottieCache[path] = json;
        if (!cancelled) setData(json);
      });
    return () => {
      cancelled = true;
    };
  }, [path]);
  return (
    <div className="relative rounded-2xl overflow-hidden w-full min-h-[260px] lg:w-[444px] lg:h-[318px] lg:min-h-0 lg:shrink-0">
      {data && (
        <Lottie
          animationData={data}
          loop
          autoplay
          rendererSettings={{ preserveAspectRatio: "xMidYMid slice" }}
          className="absolute inset-0 w-full h-full"
        />
      )}
    </div>
  );
};

const CreativeWorkspaceOperationSection = () => {
  const [active, setActive] = useState("adCreation");
  const [isInViewport, setIsInViewport] = useState(false);

  const ref = useRef(null);
  const tabDatas = tabContent;

  const checkViewport = () => {
    if (!ref.current) return;

    const rect = ref.current.getBoundingClientRect();
    const visible =
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= document.documentElement.clientHeight &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth);
    setIsInViewport(visible);
  };

  useEffect(() => {
    let timer = null;
    if (active === "brand") return;
    if (isInViewport) {
      timer = setInterval(() => {
        setActive((prev) =>
          prev === "adCreation"
            ? "distribution"
            : prev === "distribution"
              ? "intelligence"
              : prev === "intelligence"
                ? "brand"
                : null,
        );
      }, 4000);
    }

    window.addEventListener("scroll", checkViewport);
    return () => {
      window.removeEventListener("scroll", checkViewport);
      if (timer) clearInterval(timer);
    };
  }, [active, isInViewport]);
  return (
    <section className="px-4 sm:px-6 lg:px-20   pt-14 lg:pt-28 pb-[70px] lg:pb-28">
      <header className="max-w-3xl ">
        <h2 className="text-[24px] leading-[1.15]  sm:text-[30px] md:text-[44px] lg:text-5xl 3xl:text-[64px] 4xl:text-[80px] font-medium tracking-[-0.5px]">
          Your creative operation.
          <br />
          In one <span className="text-[#2626AE]">workspace.</span>
        </h2>
        <p className=" lg:text-[#262930] text-[#6D6D6D] text-[14px]  sm:text-[16px] lg:text-lg 3xl:text-xl 4xl:text-2xl max-w-[350px] lg:max-w-[490px] mt-4 sm:mt-6 font-normal tracking-[-0.3px]">
          Manage generation, collaboration, approvals, assets, and campaigns
          from a centralized AI workspace built for modern marketing teams.
        </p>
      </header>

      <div ref={ref} className="relative -mr-4 sm:mr-0 mt-6 lg:mt-8">
        <div className="flex lg:flex-wrap w-full [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] overflow-x-auto overflow-y-hidden gap-3">
          {tabDatas.map((tab) => {
            const isActive = active === tab.id;

            return (
              <button
                key={tab.id}
                onClick={() => setActive(tab.id)}
                className={`relative w-fit lg:overflow-hidden px-5 py-2 rounded-full text-[12px] lg:text-sm font-medium border transition-colors duration-[1400ms] ease-out ${
                  isActive
                    ? "text-white border-transparent"
                    : "text-[#364153] border-[#0000001A]/10 hover:bg-zinc-50"
                }`}
              >
                <span
                  aria-hidden
                  className={`absolute inset-0 rounded-full bg-gradient-to-b from-[#0A0A4C] to-[#3232A4] transition-opacity duration-[1400ms] ease-out ${
                    isActive ? "opacity-100" : "opacity-0"
                  }`}
                />
                <span className="relative whitespace-nowrap">{tab.label}</span>
              </button>
            );
          })}
        </div>
        <div className="pointer-events-none lg:hidden absolute bg-[#F5F5F7] top-1/2 right-0 -translate-y-1/2 w-10 h-40 blur-xl" />
      </div>
      {tabDatas.map((tab) => {
        return (
          <div key={tab.id}>
            {tab.id === active && (
              <div
                key={active}
                className="tab-fade-in grid lg:flex lg:flex-row lg:justify-center lg:items-stretch gap-3 lg:gap-5 mt-6"
              >
                {/* Services */}
                <div className="bg-gradient-to-b from-[#F4F4F6] to-[#EFF6FF] rounded-[24px] p-6 lg:flex-1 lg:min-w-0 lg:h-[318px]">
                  <h3 className=" text-xs lg:text-sm xl:text-lg font-medium text-[#0A0A0A] mb-5">
                    Service
                  </h3>

                  <div className="flex flex-wrap gap-2.5">
                    {tab.services.map((s) => (
                      <span
                        key={s}
                        className="bg-[#FFFFFF] rounded-md px-3 py-1 xl:py-2  text-[10px] lg:text-[12px] xl:text-[14px] text-[#364153]"
                      >
                        {s}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Middle */}
                <MiddleLottie path={tab.middle} />

                {/* CTA */}
                <div className="bg-white border border-zinc-200 rounded-[24px] p-6 flex flex-col lg:flex-1 lg:min-w-0 lg:h-[318px]">
                  <div className="flex items-center gap-2 text-[#00A63E] text-[13px]">
                    <CheckCircle2Icon
                      className="xl:w-4 xl:h-4  w-3.5 h-3.5  "
                      strokeWidth={2.5}
                    />

                    <span className="text-[#364153] font-light leading-[1.15] text-[12px]  xl:text-[14px] lg:font-normal">
                      Trusted by modern creative teams
                    </span>
                  </div>

                  <h3 className=" xl:text-[20px]  md:text-[16px] font-medium lg:font-semibold text-[#0E0B1A] mt-4 leading-[1.25] tracking-[-0.3px]">
                    {tab.last.title}
                  </h3>

                  <p className="text-zinc-600 font-[350] text-[12px] mb-6 lg:mb-0 xl:text-[14px] mt-3">
                    {tab.last.desc}
                  </p>

                  <a
                    href={tab.last.href}
                    target="_blank"
                    className="btn-primary flex items-center justify-center text-[12px] xl:text-[14px]  mt-2 xl:mt-auto w-full"
                  >
                    {tab.last.cta}
                  </a>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </section>
  );
};

export default CreativeWorkspaceOperationSection;
